import { Card, CardBody } from '@components/card/card';
import NeatixModal from '@components/Modal/NeatixModal';
import Loading from '@components/Shared/Loader/Loading';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useEffect, useState } from 'react';
import AddAbsence from './AddAbsence';

import { useTranslation } from 'react-i18next';

import { DeleteAbsenceAttendance, GetAbsenceAttendance } from '@store/HR/Person/absenceSlice';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import { usePermissions } from '@Helper/Permisstions/usePermissions';
import showConfirmation from '@components/Shared/Alert/Confirmation';
import ViewAbsence from './ViewAbsence';

const AbsenceTab: React.FC<{ personId: string }> = ({ personId }) => {
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [totalAbsenceDays, setTotalAbsenceDays] = useState<number>(0);
  const [usedAbsenceDays, setUsedAbsenceDays] = useState<number>(0);
  const [remainingAbsenceDays, setRemainingAbsenceDays] = useState<number>(0);

  const [show, setShow] = useState(false);
  const [absence, setAbsence] = useState(null);

  const dispatch = useAppDispatch();
  const { HasPermission } = usePermissions();
  const { t, i18n } = useTranslation();

  const { AbsenceAttendanceList: data, loading, error } = useAppSelector(s => s.Absence);

  useEffect(() => {
    if (personId) {
      dispatch(GetAbsenceAttendance(personId as any));
    }
  }, [dispatch, personId]);

  useEffect(() => {
    // حساب إحصائيات الإجازة
    if (data?.historyAttendances) {
      const total = data.historyAttendances.reduce((sum: any, item: any) => sum + item.absenceDays, 0);
      const used = data.historyAttendances
        .filter((item: any) => !item.increaseFlag)
        .reduce((sum: any, item: any) => sum + item.absenceDays, 0);
      const remaining = total - used;

      setTotalAbsenceDays(total);
      setUsedAbsenceDays(used);
      setRemainingAbsenceDays(remaining);
    }
  }, [data]);

  const handleAddSuccess = (): void => {
    setShowAddModal(false);
    dispatch(GetAbsenceAttendance(personId as any)); // إعادة تحميل البيانات
  };
  const DeleteAbsence = (item: any) => {
    showConfirmation(t('MsgConfirm'), () => {
      dispatch(DeleteAbsenceAttendance(item.id))
        .unwrap()
        .then(() => {
          dispatch(GetAbsenceAttendance(personId as any));
          // إظهار رسالة نجاح الحذف
          CustomAlert({ action: 'Delete' });
        })
        .catch(error => {
          console.error('Delete error:', error);
          CustomAlert({ action: 'Error', msg: t('DeleteError') });
        })
        .finally(() => {});
    });
  };

  return (
    <>
      <Loading loading={loading} error={error}>
        <div>
          {/* بطاقات إحصائيات الإجازة */}

          <div className='row mb-4'>
            {/* الإجازات الموافق عليها */}
            <div className='col-md-4'>
              <Card className='bg-success bg-opacity-10 border-success border-0 shadow-sm'>
                <CardBody className='text-center py-4'>
                  <i className='fa fa-check-circle fa-2x text-success mb-3'></i>
                  <h3 className='text-success mb-1'>{usedAbsenceDays}</h3>
                  <p className='text-muted mb-0'>الإجازات الموافق عليها</p>
                </CardBody>
              </Card>
            </div>

            {/* الإجازات قيد المراجعة */}
            <div className='col-md-4'>
              <Card className='bg-warning bg-opacity-10 border-warning border-0 shadow-sm'>
                <CardBody className='text-center py-4'>
                  <i className='fa fa-hourglass-half fa-2x text-warning mb-3'></i>
                  <h3 className='text-warning mb-1'>{remainingAbsenceDays}</h3>
                  <p className='text-muted mb-0'>الإجازات قيد المراجعة</p>
                </CardBody>
              </Card>
            </div>

            {/* الإجازات المرفوضة أو المستخدمة */}
            <div className='col-md-4'>
              <Card className='bg-danger bg-opacity-10 border-danger border-0 shadow-sm'>
                <CardBody className='text-center py-4'>
                  <i className='fa fa-times-circle fa-2x text-danger mb-3'></i>
                  <h3 className='text-danger mb-1'>{totalAbsenceDays}</h3>
                  <p className='text-muted mb-0'>الإجازات المرفوضة / المنتهية</p>
                </CardBody>
              </Card>
            </div>
          </div>

          {/* رأس الجدول مع زر الإضافة */}
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h5 className='mb-0'>
              <i className='fa fa-history me-2 text-theme'></i>
              سجل الإجازات
            </h5>
            <button className='btn btn-theme' onClick={() => setShowAddModal(true)}>
              <i className='fa fa-plus me-2'></i>
              إضافة إجازة
            </button>
          </div>

          {/* جدول سجل الإجازات */}
          <Card className='mb-4'>
            <div className='card-header d-flex justify-content-between align-items-center'>
              <h6 className='mb-0'>
                <i className='fas fa-user-times me-2'></i>
                سجل طلبات الإجازة
                <span className='badge bg-primary ms-2'>الإجازات المسجلة</span>
              </h6>
              <div>
                <span className='badge bg-theme me-2'>إجمالي الطلبات: {data?.historyAttendances?.length || 0}</span>
                <span className='badge bg-success'>الأيام المستخدمة: {usedAbsenceDays}</span>
              </div>
            </div>

            <div className='card-body p-0'>
              <div className='table-responsive'>
                <table className='table table-hover mb-0'>
                  <thead className='table-actived fw-bold'>
                    <tr>
                      <th className='text-center small'>نوع الإجازة</th>
                      <th className='text-center small'>تاريخ البداية</th>
                      <th className='text-center small'>تاريخ النهاية</th>
                      <th className='text-center small'>عدد الأيام</th>
                      <th className='text-center small'>الشخص المخوّل</th>
                      <th className='text-center small'>الحالة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.historyAttendances?.length > 0 ? (
                      data.historyAttendances.map((item: any) => (
                        <tr key={item.id}>
                          <td className='text-center align-middle'>
                            <strong>{i18n.language === 'en' ? item.absenceName : item.absenceName2}</strong>
                          </td>
                          <td className='text-center align-middle'>
                            <strong>{item.dateStart?.split('T')[0]}</strong>
                          </td>
                          <td className='text-center align-middle'>
                            <strong>{item.dateEnd?.split('T')[0]}</strong>
                          </td>
                          <td className='text-center align-middle'>
                            <span className='badge bg-info fs-6'>{item.absenceDays}</span>
                          </td>
                          <td className='text-center align-middle'>
                            {item.authorisingName || item.authorisingName2 || '-'}
                          </td>
                          <td className='align-middle text-center'>
                            {HasPermission('ViewHrPerAbsence') && (
                              <button
                                onClick={() => {
                                  setAbsence(item);
                                  setShow(true);
                                }}
                                type='button'
                                className='btn btn-sm btn-outline-theme me-2'
                                title={t('ViewDetails')}
                              >
                                {/* <i className='fas fa-eye fa-fw'></i> */}
                                {t('Show')}
                              </button>
                            )}

                            {HasPermission('DeleteHrPerAbsence') && (
                              <button
                                type='button'
                                className='btn btn-sm  btn-outline-danger'
                                onClick={() => DeleteAbsence(item)}
                                title={t('Delete')}
                              >
                                {t('Delete')}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className='text-center py-4 text-muted'>
                          <i className='fa fa-inbox fa-2x mb-2'></i>
                          <br />
                          لا توجد سجلات للإجازة
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className='card-footer d-flex justify-content-between align-items-center'>
              <div className='text-muted small'>
                عرض {data?.historyAttendances?.length || 0} من {data?.historyAttendances?.length || 0} طلب
              </div>
              <div className='text-theme fw-bold'>الأيام المتبقية: {remainingAbsenceDays} يوم</div>
            </div>
          </Card>
        </div>
      </Loading>
      <NeatixModal show={showAddModal} setShow={setShowAddModal} size='lg' title='إضافة طلب إجازة' centered>
        <AddAbsence personId={personId} setReferenceId={undefined} />
      </NeatixModal>

      <NeatixModal show={show} setShow={setShow} bodyClassName='p-0' size='lg' title={t('AbsenceDetails')} centered>
        {absence && <ViewAbsence ReferenceId={absence.id} />}
      </NeatixModal>
    </>
  );
};

export default AbsenceTab;
